import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SearchableSelect from '../../components/common/SearchableSelect';
import Label from '../../components/form/Label';
import {
  getIntakePrimaryContact,
  getIntakeRestaurantName,
  type OnboardingIntake,
  type OnboardingPrefillTarget,
} from '../../types/onboardingIntake';
import { api, apiEndpoints } from '../../utils/apiService';

interface OnboardingIntakePrefillControlProps {
  target: OnboardingPrefillTarget;
  disabled?: boolean;
  onApply: (payload: Record<string, any>, overwrite: boolean) => void;
}

const parseIntakes = (data: any): OnboardingIntake[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.intakes)) return data.intakes;
  return [];
};

export default function OnboardingIntakePrefillControl({
  target,
  disabled = false,
  onApply,
}: OnboardingIntakePrefillControlProps) {
  const [intakes, setIntakes] = useState<OnboardingIntake[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const options = useMemo(
    () =>
      intakes.map((intake) => {
        const contact = getIntakePrimaryContact(intake);
        return {
          value: intake.id,
          label: `${getIntakeRestaurantName(intake) || `Intake #${intake.id}`} (ID: ${intake.id})${
            contact.email ? ` - ${contact.email}` : ''
          }`,
        };
      }),
    [intakes],
  );

  const fetchIntakes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(apiEndpoints.getOnboardingIntakes());
      if (response?.ok) {
        setIntakes(parseIntakes(await response.json()));
      } else {
        setIntakes([]);
        setError('Failed to load onboarding intakes.');
      }
    } catch {
      setIntakes([]);
      setError('Failed to load onboarding intakes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIntakes();
  }, [fetchIntakes]);

  const applyPrefill = async () => {
    if (!selectedId) {
      setError('Select an onboarding intake first.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.get(
        apiEndpoints.getOnboardingIntakePrefill(selectedId, target),
      );
      if (!response?.ok) {
        setError('Failed to load onboarding intake prefill.');
        return;
      }
      const data = await response.json();
      onApply(
        (data.payload || data.fields || data) as Record<string, any>,
        overwrite,
      );
    } catch {
      setError('Failed to load onboarding intake prefill.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-brand-100 bg-brand-50/70 p-4 dark:border-brand-900/40 dark:bg-brand-900/10">
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
        <div>
          <Label>Populate from Onboarding Intake</Label>
          <SearchableSelect
            options={options}
            value={selectedId}
            onChange={setSelectedId}
            placeholder={loading ? 'Loading intakes...' : 'Select an intake'}
            disabled={disabled || loading}
          />
          <label className="mt-3 flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600"
            />
            <span>
              Overwrite existing fields instead of filling only blanks
            </span>
          </label>
        </div>
        <button
          type="button"
          onClick={applyPrefill}
          disabled={disabled || loading || !selectedId}
          className="rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
        >
          {loading ? 'Applying...' : 'Apply Prefill'}
        </button>
      </div>
      {error && (
        <div className="mt-3 rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-600 dark:border-error-800 dark:bg-error-900/20">
          {error}
        </div>
      )}
    </div>
  );
}
